/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClinic } from "../../../../../contexts/ClinicContext";
import { NewReservationPetType } from "../../../../../models/calendar/NewReservationPetType";
import { SavedReservationProgress } from "../../../../../models/calendar/SavedReservationProgress";
import { isFeatureAvailable, makeListSentenceFromWords } from "../../../../../util/helperFunctions";
import {
  appointmentToRescheduleKey,
  deleteFromLocalStorage,
  saveReservationInProgress,
} from "../../../../../util/LocalStorageVariables";
import { HighlightedText } from "../../../../../util/StringUtils";
import { petGenders, petSpecies, strings } from "../../../../Strings/Strings";
import { ClinicPetAndOwnerResponse } from "../../../../../models/pet/ClinicPetAndOwnerResponse";
import { PublicPhoneResponse } from "../../../../../models/contact/PublicPhoneResponse";
import TodoModal from "../../../../../pages/Todo/TodoModal";
import { PetProfileTabs } from "../../../../../pages/Pet/PetProfile/ClinicPetProfile";
import { Feature } from "../../../../../models/clinic/Feature";
import PetProfilePicture from "../../../../../components/Pictures/Pet/PetProfilePicture";
import { Heart } from "../../../../Icons/Heart";
import { ClinicFeature } from "../../../../../models/clinic/ClinicFeature";
import Dropdown from "../../../../../components/Dropdown/Dropdown";
import Button from "../../../../../components/Button";
import { ElipsisVertical } from "../../../../Icons/ElipsisVertical";

interface Props {
  dropDirection: any;
  line: ClinicPetAndOwnerResponse;
  query: string;
}

const SearchResultRow: React.FC<Props> = ({ line, query }: Props) => {
  const [newTodo, setNewTodo] = useState<boolean>(false);
  const { clinic } = useClinic();
  const navigate = useNavigate();

  const startNewReservation = (petAndOwner: ClinicPetAndOwnerResponse) => {
    const reservation: SavedReservationProgress = {
      petState: {
        state: NewReservationPetType.SELECTED,
        pet: petAndOwner.pet,
        petOwner: petAndOwner.petOwner,
      },
      petStatePermanent: true,
      symptoms: [],
    };

    saveReservationInProgress(reservation);
    deleteFromLocalStorage(appointmentToRescheduleKey);

    navigate("/");
  };

  return (
    <div className="flex items-center px-4 py-3">
      <div className="flex items-start">
        <div>
          <div style={{ width: "30px", height: "30px" }}>
            <PetProfilePicture species={line.pet.clinicPetDetails.species} petId={line.pet.clinicPetDetails.id} />
          </div>
        </div>
        <div className="px-3 text-sm" style={{ whiteSpace: "nowrap" }}>
          <div className="flex items-center">
            <HighlightedText text={line.pet.clinicPetDetails.name} highlights={query.split(" ")} />
            {line.pet.clinicPetDetails?.deceased ? (
              <div>
                <Heart className="ml-2 h-5 w-5 flex-shrink-0" variant="solid" />
              </div>
            ) : (
              <></>
            )}
          </div>
          <div>
            {makeListSentenceFromWords([
              petSpecies[line.pet.clinicPetDetails.species],
              petGenders[line.pet.clinicPetDetails.gender],
              line.pet.clinicPetDetails.breed,
              line.pet.clinicPetDetails.lastMedicalRecordWeight &&
                `${line.pet.clinicPetDetails.lastMedicalRecordWeight} kg`,
            ])}
          </div>
          <div className="flex italic">
            <HighlightedText text={line.petOwner.petOwnerDetails.fullName} highlights={query.split(" ")} />
            {line.petOwner.petOwnerDetails.personalId && (
              <div className="text-sm ml-2">
                <HighlightedText text={line.petOwner.petOwnerDetails.personalId} highlights={query.split(" ")} />
              </div>
            )}
            {line.petOwner.petOwnerDetails.phones?.map((phone: PublicPhoneResponse) => (
              <div className="text-sm ml-2">
                <HighlightedText text={phone.value} highlights={query.split(" ")} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <Dropdown className="min-w-48">
        <Dropdown.Toggle />
        <Dropdown.Content>
          <>
            {clinic?.features.includes(Feature.RESERVATION) && (
              <li>
                <Button
                  className="dropdown-item"
                  onClick={() => {
                    startNewReservation(line);
                  }}
                  variant="link"
                >
                  {strings.newReservation}
                </Button>
              </li>
            )}
            <li>
              <Button
                className="dropdown-item"
                onClick={() => {
                  setNewTodo(true);
                }}
                variant="link"
              >
                {strings.newTask}
              </Button>
            </li>
            {clinic &&
              clinic?.features.includes(Feature.PRESCRIPTION) &&
              isFeatureAvailable(clinic, ClinicFeature.PRESCRIPTION) && (
                <li>
                  <Link className="dropdown-item" to={`/prescription/${line.pet.clinicPetDetails.id}`}>
                    {strings.newPrescription}
                  </Link>
                </li>
              )}
            {clinic &&
              clinic?.features.includes(Feature.PRESCRIPTION) &&
              isFeatureAvailable(clinic, ClinicFeature.PRESCRIPTION) && (
                <li>
                  <Link
                    className="dropdown-item"
                    to={`/prescriptions/pet/${line.pet.clinicPetDetails.id}`}
                    state={{ petName: line.pet.clinicPetDetails.name }}
                  >
                    {strings.prescriptions}
                  </Link>
                </li>
              )}
            <li>
              <Link
                className="dropdown-item"
                to={`/clinic-pet-profile/${line.pet.clinicPetDetails.id}/${PetProfileTabs.basic}`}
              >
                {strings.petProfile}
              </Link>
            </li>
            <li>
              <Link className="dropdown-item" to={`/pet-owner-profile/${line.petOwner.petOwnerDetails.id}`}>
                {strings.ownerProfile}
              </Link>
            </li>
          </>
        </Dropdown.Content>
      </Dropdown>

      <TodoModal
        onHide={() => {
          setNewTodo(false);
        }}
        show={newTodo}
        todoForPet={line}
      />
    </div>
  );
};

export default SearchResultRow;
